<template>
  <div>
    <LoadingCircle v-if="isLoading && !showUserInfo" />
    <div v-if="showUserInfo">
      <h1>View user</h1>

      <h2 class="mt-10 mb-4">User:</h2>

      <v-row class="ml-2" dense>
        <v-col><b>Email:</b> {{ userProfile.email }}</v-col>
        <v-col><b>Role:</b> {{ userProfile.role }}</v-col>
      </v-row>
      <v-row class="ml-2" dense>
        <v-col>
          <b>Locked out: </b>
          {{ userProfile.lockedOut ? StringBoolean.Yes : StringBoolean.No }}
        </v-col>
      </v-row>

      <v-divider class="my-6" />
      <h2 class="mb-4">Trust group:</h2>
      <UserTrustGroupInfo
        v-if="trustGroup?.id"
        :trust-group="trustGroup"
        @remove-user-from-trust-group="removeTrustGroup"
      />
      <div v-else>User is not a member of any trust group.</div>

      <PPrimaryButton
        class="mt-5 mb-10"
        @click="showLinkCompany = true"
        v-if="!showLinkCompany && !trustGroup?.id"
        >Add user to a trust group</PPrimaryButton
      >
      <v-divider class="my-6" />
      <v-row class="mt-2 ml-0" v-if="!editingUser && !showLinkCompany">
        <PSecondaryButton @click="exitToUserSearch">Back</PSecondaryButton>
        <v-spacer />
        <PSecondaryButton class="mx-5" @click="unlockAndResetSuspension">
          Unlock and reset suspension count
        </PSecondaryButton>
        <PSecondaryButton class="mx-5" @click="lockOrUnlockUser">
          {{ userProfile.lockedOut ? "Unlock user" : "Lock out user" }}
        </PSecondaryButton>
        <PSecondaryButton class="mx-5" @click="showDeleteDialog = true">
          Delete User
        </PSecondaryButton>
        <PPrimaryButton class="mx-5" @click="editingUser = true">
          Change user role
        </PPrimaryButton>
      </v-row>

      <GenericPopup
        :is-visible="showDeleteDialog"
        accept-text="Delete User"
        @cancel="showDeleteDialog = false"
        @accept="deleteUser()"
      >
        <p>Are you sure you want to delete this user?</p>
        Email: <strong>{{ userProfile.email }}</strong>
        <br />
        Role: <strong>{{ userProfile.role }}</strong>
      </GenericPopup>
    </div>

    <EditUser
      v-if="editingUser"
      :user-profile="userProfile"
      @updated="userProfile = { ...$event }"
      @cancel="editingUser = false"
    />
    <LinkTrustGroup
      v-if="showLinkCompany"
      :user-profile="userProfile"
      @linked="displayUserInfo()"
      @canceled="showLinkCompany = false"
    />
  </div>
</template>

<script setup lang="ts">
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import { ref, onMounted, type Ref } from "vue";
import { UserProfile } from "@/models/userProfile";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import { genericError } from "@/modules/utilities";
import {
  getUserProfile,
  deleteUserProfile,
  lockOutUserProfile,
  unlockUserProfile,
  unlockAndResetSuspensionUserProfile,
} from "@/services/userProfileAdminService";
import EditUser from "@/views/admin-view/EditUser.vue";
import LinkTrustGroup from "@/views/admin-view/LinkTrustGroup.vue";
import GenericPopup from "@/components/GenericPopup.vue";
import UserTrustGroupInfo from "@/views/admin-view/UserTrustGroupInfo.vue";
import { StringBoolean } from "@/models/enum/stringBoolean";
import { TrustGroup } from "@/models/trustGroup";
import {
  getTrustGroup,
  removeMemberFromTrustGroup,
} from "@/services/trustGroupAdminService";
import { AddTrustGroupUserDto } from "@/models/dto/addTrustGroupUserDto";
import { useSnackBarStore } from "@/stores/snackBarStore";

const snackbarStore = useSnackBarStore();

const showUserInfo = ref(false);
const isLoading = ref(false);
const foundCompany = ref(false);
const editingUser = ref(false);

const showDeleteDialog = ref(false);
const showLinkCompany = ref(false);

const userProfile = ref(new UserProfile());
const trustGroup: Ref<TrustGroup | undefined> = ref(new TrustGroup());

const props = defineProps<{
  email: string;
}>();

const emit = defineEmits<{
  (e: "error"): void;
  (e: "exit"): void;
}>();

onMounted(async () => {
  await displayUserInfo();
});

function exitToUserSearch() {
  emit("exit");
}

async function deleteUser() {
  try {
    showDeleteDialog.value = false;
    await deleteUserProfile(props.email);
    snackbarStore.showSuccess("Successfully deleted user");
  } catch (error) {
    snackbarStore.showError(genericError);
  }
}

async function displayUserInfo() {
  isLoading.value = true;

  const successUser = await getUserInfo();
  if (!successUser) {
    snackbarStore.showError("User not found");
    return;
  }

  if (userProfile.value.companyId) {
    const successCompany = await getTrustGroupInfo();

    if (!successCompany) {
      snackbarStore.showError("Trust group not found");

      return;
    }
    foundCompany.value = true;
  } else {
    foundCompany.value = false;
  }

  showUserInfo.value = true;
  showLinkCompany.value = false;
  isLoading.value = false;
}

async function getUserInfo() {
  try {
    userProfile.value = await getUserProfile(props.email);
    return true;
  } catch (error) {
    return false;
  }
}

async function getTrustGroupInfo() {
  try {
    trustGroup.value = await getTrustGroup(userProfile.value.email);
    return true;
  } catch (error) {
    return false;
  }
}

async function removeTrustGroup(trustGroupInput: TrustGroup) {
  try {
    let addUserToTrustGroup = new AddTrustGroupUserDto();
    addUserToTrustGroup.email = userProfile.value.email;
    addUserToTrustGroup.trustGroupId = trustGroupInput.id;
    await removeMemberFromTrustGroup(addUserToTrustGroup);
    trustGroup.value = new TrustGroup();
  } catch (error) {
    snackbarStore.showError(genericError);
  }
}

async function lockOrUnlockUser() {
  try {
    userProfile.value = userProfile.value.lockedOut
      ? await unlockUserProfile(userProfile.value)
      : await lockOutUserProfile(userProfile.value);
  } catch (error) {
    snackbarStore.showError(genericError);
  }
}

async function unlockAndResetSuspension() {
  try {
    userProfile.value = await unlockAndResetSuspensionUserProfile(
      userProfile.value.email
    );
  } catch (error) {
    snackbarStore.showError(genericError);
  }
}
</script>
